<template>
  <div>
    <Preloader
      v-if="loading"
    />
    <VRow v-else class="h-100">
      <VCol cols="12" class="d-flex flex-column">
        <VacancyStepTitle
          v-if="vacancy"
          :progress="40"
          :vacancy="vacancy"
          :step="2"
        >
          {{ $t('step') }}
        </VacancyStepTitle>
        <div class="mt-2 mt-md-0 d-flex justify-space-between align-center">
          <a v-if="!disabled && !isVacancyCompiling" @click="openChangeAiInterviewerDialog" class="link-dotted">
            {{ $t('changeAvatar') }}
          </a>
        </div>
        <VacancyAiQuestionsForm
          v-if="vacancy"
          :vacancy="vacancy"
          :ai-interviewer="aiInterviewer"
          :maximum-ai-questions="limitedPresetQuestions"
          :selected-questions="selectedQuestions"
          :is-ai-template="isAiTemplate"
          @fetchById="fetchById"
          @goToVacancy="goToVacancy"
          @next="next"
          @setDisable="setDisable"
          @setSelectedQuestions="setSelectedQuestions"
          @resetWizardPresetid="resetWizardPresetid"
          @setLoading="isChildLoading = $event"
        >
          <template v-slot:backButton="{ disabled }">
            <VBtn
              color="success"
              outlined
              class="mr-2"
              :disabled="disabled || isVacancyCompiling"
              elevation="0"
              @click="back"
            >
              {{ $t('goBack') }}
            </VBtn>
          </template>
          <template v-slot:saveButton="{ disabled, submit }">
            <VBtn
              color="success"
              type="submit"
              class="mr-2"
              :disabled="disabled"
              elevation="0"
              @click="submit"
            >
              {{ $t('save') }}
            </VBtn>
          </template>
          <template v-slot:publishButton="{ disabled, submit }">
            <VBtn
              color="success"
              type="submit"
              :disabled="disabled"
              elevation="0"
              @click="next(submit)"
            >
              {{ $t('next') }} &rarr;
            </VBtn>
          </template>
        </VacancyAiQuestionsForm>

        <VacancyPresetInterviewers
          v-if="avatars"
          :key="showDialog"
          v-model="showDialog"
          @updateFromLocalStorage="updateFromLocalStorage"
          @selected="handleAvatarSelected"
          :vacancy-id="vacancyId"
          :preset="'aiInterview'"
          :avatars="avatars"
          :current-avatar="aiInterviewer"
        />
      </VCol>
    </VRow>
    <VDialog
      v-model="dialogMaximumVacancies"
      persistent
      max-width="500"
    >
      <DialogLimitedVacancies
        :plan-title="planTitle"
        :limited-vacancies="limitedVacancies"
        @close="cancelDialogMaximumVacancies"
      />
    </VDialog>
  </div>
</template>

<script>
import VacancyAiQuestionsForm from '@/components/Vacancy/VacancyAiQuestionsForm.vue';
import {
  ref,
  computed,
  toRefs,
  onMounted,
  watch,
} from '@vue/composition-api';
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle.vue';
import useVacancies from '@/compositions/useVacancies';
import Preloader from '@/components/Preloader.vue';
import Wizard from '@/orm/models/Wizard/Wizard';
import useInterviewer from '@/compositions/useInterviewer';
import useAvatars from '@/compositions/useAvatars';
import useAccount from '@/compositions/accounting/useAccount';
import { wizardSteps, interviewPresetTypes, vacancyStatus } from '@/enums';
import { useI18n } from 'vue-i18n-composable';
import VacancyPresetInterviewers from '@/components/Vacancy/VacancyPresetInterviewers.vue';
import InterviewPreset from '@/orm/models/Interview/InterviewPreset';
import DialogLimitedVacancies from '@/components/DialogLimitedVacancies.vue';

export default {
  name: 'VacancyStepAiQuestions',
  metaInfo() {
    return {
      title: this.$t('step'),
    };
  },
  components: {
    VacancyPresetInterviewers,
    VacancyAiQuestionsForm,
    VacancyStepTitle,
    Preloader,
    DialogLimitedVacancies,
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  setup(props, { root: { $router } }) {
    const { account, fetchIfNull } = useAccount();
    const { interviewers, fetchInterviewers } = useInterviewer();
    const { avatars, getAvatars } = useAvatars();
    const { locale, t } = useI18n();
    const { vacancyId } = toRefs(props);
    const {
      fetch, getVacancyById, fetchById, notArchivedVacancies, notArchivedNotDraftVacancies,
    } = useVacancies();
    const loading = ref(false);
    const disabled = ref(false);
    const showDialog = ref(false);
    const dialogMaximumVacancies = ref(false);
    const isChildLoading = ref(false);

    const vacancy = computed(() => getVacancyById(vacancyId.value));
    const aiInterviewer = ref(null);
    const selectedQuestions = ref([]);

    const isVacancyCompiling = computed(() => {
      return vacancy.value?.isAiInterviewCompiling
             || vacancy.value?.isAiInterviewRecompiling
             || vacancy.value?.isAiInterviewCompiled
             || (vacancy.value?.aiQuestionsGenerating && !vacancy.value?.aiQuestionsGenerated)
             || isChildLoading.value;
    });

    const currentInterviewerId = computed(() => (locale.value === 'en' ? 3 : 1));

    const allPresets = computed(() => InterviewPreset.query().withAllRecursive().all());
    const presetId = computed(() => {
      const wizardState = Wizard.modelState();
      return wizardState && wizardState.presetId ? wizardState.presetId : null;
    });
    const currentPreset = computed(() => allPresets.value.filter((preset) => preset.id === presetId.value)[0]);

    const presetQuestionsLength = computed(() => currentPreset.value?.counts?.questions);
    const limitedPresetQuestions = computed(() => {
      return (
        (currentPreset.value?.type === interviewPresetTypes.COMMON && presetQuestionsLength.value < 20)
        || !vacancy.value.aiInterviewPresetId
          ? 20
          : presetQuestionsLength.value + 5
      );
    });

    const isAiTemplate = computed(() => vacancy.value?.wizardState?.isAiTemplate || false);

    const planTitle = computed(() => account.value?.plan.title);
    const maximumVacancies = computed(() => account.value?.maximumVacancies);
    const trialMaximumVacancies = computed(() => account.value?.trialMaximumVacancies);
    const notArchivedVacanciesLength = computed(() => notArchivedVacancies.value?.length);
    const notArchivedNotDraftVacanciesLength = computed(() => notArchivedNotDraftVacancies.value?.length);

    const limitedVacancies = computed(() => {
      if (trialMaximumVacancies.value && account.value.trialEnabled && (trialMaximumVacancies.value <= notArchivedVacanciesLength.value)) {
        return trialMaximumVacancies.value;
      }

      if (maximumVacancies.value && (maximumVacancies.value <= notArchivedVacanciesLength.value)) {
        return maximumVacancies.value;
      }
    });

    function openDialogMaximumVacancies() {
      dialogMaximumVacancies.value = true;
    }

    function cancelDialogMaximumVacancies() {
      dialogMaximumVacancies.value = false;
    }

    function checkVacancyLimit() {
      if (vacancy.value?.status !== vacancyStatus.DRAFT) {
        return false;
      }

      const totalNotArchived = notArchivedVacanciesLength.value || 0;
      const currentVacanciesCountExcludingCurrent = Math.max(totalNotArchived - 1, 0);

      const willExceedLimit = (maximumVacancies.value && (maximumVacancies.value <= currentVacanciesCountExcludingCurrent))
          || (account.value?.trialEnabled
            && trialMaximumVacancies.value
            && (trialMaximumVacancies.value <= currentVacanciesCountExcludingCurrent));

      return Boolean(willExceedLimit);
    }

    watch(
      () => Wizard.modelState().selectedQuestions,
      (newVal) => {
        selectedQuestions.value = newVal || [];
      },
      { immediate: true },
    );

    async function goToVacancy() {
      $router.push({
        name: 'vacancy',
        params: { id: vacancyId.value },
      });
    }

    async function goToWizard() {
      $router.push({
        name: 'vacancy-preset',
        params: { vacancyId: vacancyId.value },
      });
    }

    function openChangeAiInterviewerDialog() {
      if (!aiInterviewer.value && vacancy.value) {
        const storageKey = `vacancy_${vacancy.value.id}_avatar_for_create`;
        aiInterviewer.value = localStorage.getItem(storageKey);
      }

      showDialog.value = true;
    }

    async function next(submit) {
      if (!aiInterviewer.value) {
        openChangeAiInterviewerDialog();
        return;
      }

      if (checkVacancyLimit()) {
        await fetchIfNull();
        await fetch();
        openDialogMaximumVacancies();
        return;
      }

      const result = await submit();
      if (result === false) {
        return;
      }

      await Wizard.pushNextRoute($router);
    }

    async function back() {
      await $router.push({
        name: 'vacancy-store',
        params: {
          id: vacancyId.value,
        },
      });
    }

    function setState() {
      Wizard
        .setVacancyId(vacancyId.value)
        .setInterviewerId(currentInterviewerId.value)
        .setWizardStep(wizardSteps.AI_QUESTIONS);
    }

    async function resetWizardPresetid() {
      Wizard.setPresetId(null);
      await Wizard.saveWizardState();
    }

    async function prepare() {
      try {
        setState();
        await Promise.all([Wizard.fetchData()]);
        if (!Wizard.modelState().selectedQuestions.length && presetId.value) {
          await Wizard.fetchScript();
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error in prepare:', error);
        }
        throw error;
      }
    }

    function setDisable() {
      disabled.value = true;
    }

    async function fetchAll() {
      if (!vacancy.value) {
        await fetchById(vacancyId.value);
      }
      if (!interviewers.value.length) {
        await fetchInterviewers();
      }
      await fetch();
      await prepare();
      await getAvatars();
      await fetchIfNull();
      await Wizard.fetchPresets();
    }

    function updateFromLocalStorage() {
      aiInterviewer.value = localStorage.getItem(`vacancy_${vacancy.value.id}_avatar_for_create`);
    }

    async function getFirstAvatarKey() {
      if (!avatars.value) {
        await getAvatars();
      }

      return Object.keys(avatars.value)
        .find((key) => key !== '$id' && key !== 'id') || null;
    }

    function handleAvatarSelected(interviewer) {
      if (!interviewer || !interviewer.actor_id) {
        return;
      }
      if (vacancy.value && vacancy.value.id && typeof vacancy.value.id === 'number') {
        const storageKey = `vacancy_${vacancy.value.id}_avatar_for_create`;
        localStorage.setItem(storageKey, interviewer.actor_id);
        aiInterviewer.value = interviewer.actor_id;
      }
    }

    async function setSelectedQuestions(qs) {
      selectedQuestions.value = qs;
      Wizard.setSelectedQuestions(qs);
      await Wizard.saveWizardState();
    }

    onMounted(async () => {
      loading.value = true;
      try {
        await fetchAll();
        if (vacancy.value) {
          const storageKey = `vacancy_${vacancy.value.id}_avatar_for_create`;
          if (!localStorage.getItem(storageKey)) {
            const firstAvatarKey = await getFirstAvatarKey();
            if (firstAvatarKey) {
              localStorage.setItem(storageKey, firstAvatarKey);
            }
          }
          aiInterviewer.value = localStorage.getItem(storageKey) || await getFirstAvatarKey();
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error in mounted hook:', error);
        }
      } finally {
        loading.value = false;
      }
    });

    return {
      goToWizard,
      vacancy,
      back,
      next,
      loading,
      fetchById,
      goToVacancy,
      setDisable,
      disabled,
      aiInterviewer,
      showDialog,
      openChangeAiInterviewerDialog,
      avatars,
      updateFromLocalStorage,
      handleAvatarSelected,
      limitedPresetQuestions,
      dialogMaximumVacancies,
      planTitle,
      limitedVacancies,
      cancelDialogMaximumVacancies,
      currentPreset,
      selectedQuestions,
      setSelectedQuestions,
      isAiTemplate,
      resetWizardPresetid,
      isVacancyCompiling,
      isChildLoading,
    };
  },
  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      const vacancyId = Number.parseInt(to.params.vacancyId, 10);
      const { getVacancyById, fetchById } = useVacancies();

      let vacancy = getVacancyById(vacancyId);
      if (!vacancy) {
        await fetchById(vacancyId);
        vacancy = getVacancyById(vacancyId);
      }

      if (vacancy && (vacancy.isAiInterviewCompiling || vacancy.isAiInterviewRecompiling) && !vacancy.isAiInterviewCompiled) {
        vm.$router.push({
          name: 'vacancy-publish',
          params: { vacancyId },
        });
      }
    });
  },
  beforeRouteUpdate(to, from, next) {
    const vacancyId = Number.parseInt(to.params.vacancyId, 10);
    const { getVacancyById, fetchById } = useVacancies();

    let vacancy = getVacancyById(vacancyId);
    if (!vacancy) {
      fetchById(vacancyId).then(() => {
        vacancy = getVacancyById(vacancyId);
        if (vacancy && (vacancy.isAiInterviewCompiling || vacancy.isAiInterviewRecompiling) && !vacancy.isAiInterviewCompiled) {
          this.$router.push({
            name: 'vacancy-publish',
            params: { vacancyId },
          });
          return;
        }
        next();
      });
      return;
    }

    if (vacancy && (vacancy.isAiInterviewCompiling || vacancy.isAiInterviewRecompiling) && !vacancy.isAiInterviewCompiled) {
      this.$router.push({
        name: 'vacancy-publish',
        params: { vacancyId },
      });
      return;
    }

    next();
  },
};
</script>

<style lang="scss" scoped>
.q-row {
  background: #fff;
  margin-bottom: 5px;
}

.h-100 {
  height: 100%;
}
</style>

<i18n locale="en" lang="yaml">
  step: "Interview questions list"
  next: Next
  lostInfoWarning: Changes not saved will be lost. Are you sure?
  continue: Continue editing
  goBack: Go back
  returnToMaster: Back to the Template Selection wizard
  returnToVacancy: Go back to the job
  congrats: You have completed question selection.
  anyTime: You can delete a question at any time, swap questions or add a new question.
  pleaseWaitAiLoaderText: 'Artificial intelligence is in the process of selecting questions for your job.
  The expected wait time is 3-5 minutes. Please hold on. If necessary, you can go to other sections.
  The job will be available in the jobs section after completion of the preparation process.'
  pleaseWaitAiLoaderTextVoice: 'Artificial intelligence is in the process of voicing questions for your job.
  We expect it to take 3-5 minutes, please wait... If necessary, you can go to other sections.
  The job will be available in the jobs section after completion of the preparation process'
  pleaseWaitAiLoaderTextRecompiling: 'Artificial intelligence is in the process of voicing additional questions for your job.
  The expected time is 3-5 minutes, please wait… If necessary, you can go to other sections.
  The job will be available in the jobs section after completing the preparation process.'
  pleaseWaitAiLoaderTextCopy: 'The job is in the process of copying. The expected time is 1-3 minutes. Please wait…'
  save: Save
  changeAvatar: Change the avatar
  addQuestionTextError: Add at least one question for the vacancy.
</i18n>

<i18n locale="ru" lang="yaml">
  step: "Список вопросов для интервью"
  next: Далее
  lostInfoWarning: Все изменения будут потеряны. Вы уверены?
  continue: Продолжить редактирование
  goBack: Назад
  returnToMaster: Вернуться в мастер выбора шаблона
  returnToVacancy: Вернуться к вакансии
  congrats: Ура! ИИ завершил подборку вопросов для вашей вакансии
  anyTime: В любой момент вы можете удалить вопрос, поменять вопросы местами или добавить новый.
  pleaseWaitAiLoaderText: 'Искусственный интеллект в процессе подбора вопросов для вашей вакансии.
  Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
  Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
  pleaseWaitAiLoaderTextVoice: 'Искусственный интеллект в процессе озвучивания вопросов для вашей вакансии.
  Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
  Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
  pleaseWaitAiLoaderTextRecompiling: 'Искусственный интеллект в процессе озвучивания дополнительных вопросов для вашей вакансии.
  Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости Вы можете перейти в другие разделы.
  Вакансия будет доступна в разделе вакансий, после завершения процесса подготовки.'
  pleaseWaitAiLoaderTextCopy: 'Вакансия в процессе копирования. Ожидаемое время 1-3 минуты. Пожалуйста, подождите…'
  save: Сохранить
  changeAvatar: Изменить аватар
  addQuestionTextError: Добавьте минимум один вопрос для вакансии.
</i18n>
